"use client";

import { c as _c } from "react/compiler-runtime";
import { formatLabel } from "@/utils/string";
import { memo, useId } from "react";
import { FieldDescription } from "./FieldDescription";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-sql";
interface PropertySchema {
  title?: string;
  description?: string;
  required?: boolean;
  maxLength?: number;
  [key: string]: unknown;
}
interface SqlEditorInputProps {
  name: string;
  property: PropertySchema;
  value: string;
  onChange: (value: string) => void;
  hideLabel?: boolean;
}
const SQL_PLACEHOLDER = `SELECT * FROM users WHERE age > ?`;
function SqlEditorInput(t0) {
  const $ = _c(30);
  const {
    name,
    property,
    value: t1,
    onChange,
    hideLabel: t2
  } = t0;
  const value = t1 === undefined ? "" : t1;
  const hideLabel = t2 === undefined ? false : t2;
  const editorId = useId();
  const isOverLimit = property.maxLength && value.length > property.maxLength;
  const isEmpty = !value.trim();
  let t3;
  if ($[0] !== editorId || $[1] !== hideLabel || $[2] !== name || $[3] !== property.required || $[4] !== property.title) {
    t3 = !hideLabel && <label htmlFor={editorId} className="field-label block">{property.title || formatLabel(name)}{property.required && <span className="ml-1 text-red-500">*</span>}</label>;
    $[0] = editorId;
    $[1] = hideLabel;
    $[2] = name;
    $[3] = property.required;
    $[4] = property.title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== editorId || $[7] !== property.description) {
    t4 = property.description && <FieldDescription id={`${editorId}-description`} text={property.description} />;
    $[6] = editorId;
    $[7] = property.description;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !hideLabel ? editorId : undefined;
  const t6 = property.description ? `${editorId}-description` : undefined;
  let t7;
  if ($[9] !== isEmpty) {
    t7 = isEmpty && <div className="text-ds-text-disabled dark:text-ds-text-subtle text-2-5xs pointer-events-none absolute inset-0 p-3 font-mono leading-normal" aria-hidden="true"><pre style={{
        margin: 0
      }}>{SQL_PLACEHOLDER}</pre></div>;
    $[9] = isEmpty;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      fontFamily: "\"Fira Code\", \"Fira Mono\", Menlo, Monaco, \"Courier New\", monospace",
      fontSize: 13,
      minHeight: "120px"
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== editorId || $[13] !== onChange || $[14] !== value) {
    t9 = <Editor value={value} onValueChange={onChange} highlight={_temp} padding={12} style={t8} className="code-editor" preClassName="text-ds-text-primary dark:text-zinc-200" textareaClassName="focus:outline-none caret-zinc-900 dark:caret-white" textareaId={editorId} />;
    $[12] = editorId;
    $[13] = onChange;
    $[14] = value;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t9) {
    t10 = <div className="border-ds-neutral-300 dark:border-ds-neutral-700 relative overflow-hidden rounded-md border bg-white dark:bg-[#282c34]" role="group" aria-labelledby={t5} aria-describedby={t6}>{t7}{t9}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isOverLimit || $[22] !== property.maxLength || $[23] !== value.length) {
    t11 = property.maxLength && <p className={`text-xs ${isOverLimit ? "font-medium text-red-600 dark:text-red-400" : "text-ds-text-muted"}`} role="status" aria-live="polite">{value.length} / {property.maxLength} characters{isOverLimit && <span className="ml-1">({value.length - property.maxLength} over limit)</span>}</p>;
    $[21] = isOverLimit;
    $[22] = property.maxLength;
    $[23] = value.length;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t3 || $[28] !== t4) {
    t12 = <div className="space-y-2">{t3}{t4}{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t3;
    $[28] = t4;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function _temp(code) {
  return highlight(code, languages.sql, "sql");
}
export default memo(SqlEditorInput, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value && prevProps.name === nextProps.name && prevProps.hideLabel === nextProps.hideLabel && prevProps.property.title === nextProps.property.title && prevProps.property.description === nextProps.property.description && prevProps.property.required === nextProps.property.required && prevProps.property.maxLength === nextProps.property.maxLength;
});